import { useState } from "react";
import {
  getFeatureRequests,
  createFeatureRequest,
  updateFeatureRequest,
  getVersion,
} from "../data/store";
import type { FeatureRequest, ItemStatus, ItemPriority } from "../types";

const statusColors: Record<ItemStatus, string> = {
  open: "badge-info",
  "in-progress": "badge-warning",
  completed: "badge-success",
  closed: "badge-danger",
};

const priorityColors: Record<ItemPriority, string> = {
  low: "badge-info",
  medium: "badge-warning",
  high: "badge-danger",
  critical: "badge-danger",
};

export default function FeatureRequests() {
  const [features, setFeatures] = useState(getFeatureRequests());
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<ItemStatus | "all">("all");
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium" as ItemPriority,
    author: "Randy",
  });

  const filtered = filter === "all" ? features : features.filter((f) => f.status === filter);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createFeatureRequest({ ...form, status: "open" });
    setFeatures(getFeatureRequests());
    setForm({ title: "", description: "", priority: "medium", author: "Randy" });
    setShowForm(false);
  }

  function handleStatusChange(id: string, status: ItemStatus) {
    updateFeatureRequest(id, { status });
    setFeatures(getFeatureRequests());
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2>Feature Requests</h2>
            <p>Track planned features and link them to versions when completed.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            + New Feature
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        {(["all", "open", "in-progress", "completed", "closed"] as const).map((s) => (
          <button
            key={s}
            className={`btn ${filter === s ? "btn-primary" : ""}`}
            style={filter !== s ? { background: "var(--card-bg)", border: "1px solid var(--border)" } : {}}
            onClick={() => setFilter(s)}
          >
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}
          </button>
        ))}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>New Feature Request</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                rows={3}
                style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)", resize: "vertical" }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as ItemPriority })}
                style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary">Create</button>
            <button type="button" className="btn" style={{ marginLeft: "0.5rem" }} onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Feature list */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Completed In</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => {
              const version = f.completedInVersion ? getVersion(f.completedInVersion) : null;
              return (
                <tr key={f.id}>
                  <td>
                    <strong>{f.title}</strong>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                      {f.description.substring(0, 80)}{f.description.length > 80 ? "..." : ""}
                    </div>
                  </td>
                  <td><span className={`badge ${priorityColors[f.priority]}`}>{f.priority}</span></td>
                  <td><span className={`badge ${statusColors[f.status]}`}>{f.status}</span></td>
                  <td>
                    {version ? (
                      <span className="badge badge-success">v{version.version}</span>
                    ) : (
                      <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>{new Date(f.createdAt).toLocaleDateString()}</td>
                  <td>
                    <select
                      value={f.status}
                      onChange={(e) => handleStatusChange(f.id, e.target.value as ItemStatus)}
                      style={{ padding: "0.25rem", borderRadius: "4px", border: "1px solid var(--border)", fontSize: "0.8rem" }}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p style={{ padding: "1rem", color: "var(--text-muted)", textAlign: "center" }}>
            No feature requests found.
          </p>
        )}
      </div>
    </div>
  );
}
